import React, { useCallback, useEffect } from 'react';
import { StyleSheet } from 'react-native';
import styled from '@emotion/native';
import { Menu } from 'react-native-paper';
import { useApolloClient } from '@apollo/client';
import { sand } from '@/shared/styles/colors';
import NotificationList from './NotificationList';
import useStore from '@/stores/useStore';
import useUnreadNotifications from '@/shared/hooks/useUnreadNotifications';

interface NotificationDropdownProps {
  isVisible: boolean;
  onDismiss: () => void;
  onPress: () => void;
  renderAnchor: (props: {
    onPress: () => void;
    hasUnreadNotifications: boolean;
    isVisible: boolean;
  }) => React.ReactNode;
}

const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
  isVisible,
  onDismiss,
  onPress,
  renderAnchor,
}) => {
  const apolloClient = useApolloClient();
  const setSnackbarState = useStore((state) => state.setSnackbarState);
  const { hasUnreadNotifications, refetch: refetchUnreadCount } =
    useUnreadNotifications({ disablePolling: isVisible });

  // Refetch notifications when dropdown opens
  useEffect(() => {
    if (isVisible) {
      // Evict the notifications cache to force fresh data
      apolloClient.cache.evict({
        fieldName: 'notifications',
      });
      apolloClient.cache.gc(); // Garbage collect to clean up evicted data
    }
  }, [isVisible, apolloClient]);

  // Handle notification errors
  const handleNotificationError = useCallback(() => {
    setSnackbarState({
      open: true,
      message: 'Failed to load notifications. Please try again.',
      severity: 'error',
    });
  }, [setSnackbarState]);

  // Handle dropdown dismiss with refetch (used for both menu dismiss and notification press)
  const handleDismiss = useCallback(() => {
    onDismiss();
    // Refetch unread count when dropdown is dismissed
    refetchUnreadCount();
  }, [onDismiss, refetchUnreadCount]);

  return (
    <MenuWrapper>
      <Menu
        visible={isVisible}
        onDismiss={handleDismiss}
        anchorPosition="bottom"
        contentStyle={styles.menuContent}
        style={styles.menu}
        anchor={renderAnchor({
          onPress,
          hasUnreadNotifications,
          isVisible,
        })}
      >
        <Triangle />
        <DropdownContainer>
          <NotificationList
            onNotificationPress={handleDismiss}
            onError={handleNotificationError}
            showScrollbar={true}
          />
        </DropdownContainer>
      </Menu>
    </MenuWrapper>
  );
};

export default NotificationDropdown;

const MenuWrapper = styled.View({
  position: 'relative',
});

const DropdownContainer = styled.View({
  height: 400, // Fixed height for dropdown
  width: 380, // Fixed width for dropdown
});

const Triangle = styled.View<{ right?: number; size?: number }>(
  ({ right = 20, size = 16 }) => ({
    position: 'absolute',
    top: -size,
    right: right,
    width: 0,
    height: 0,
    backgroundColor: 'transparent',
    borderStyle: 'solid',
    borderLeftWidth: size,
    borderRightWidth: size,
    borderBottomWidth: size,
    borderLeftColor: 'transparent',
    borderRightColor: 'transparent',
    borderBottomColor: sand,
    zIndex: 1,
  }),
);

const styles = StyleSheet.create({
  menu: {
    width: 380,
    minHeight: 400,
    marginTop: 24,
    transform: [{ translateX: 16 }], // Center dropdown under bell icon
  },
  menuContent: {
    backgroundColor: sand,
    borderRadius: 10,
    width: 380,
    height: 400,
    boxShadow: '0px 4px 24px rgba(0, 0, 0, 0.15)',
    padding: 0, // Remove default padding to let NotificationList handle its own
  },
});
